import React from "react";
import Layout from "./Layout/Layout";
import { Monitor, Globe, BookOpen, GraduationCap } from "lucide-react";
import { motion } from "framer-motion";

const activities = [
  {
    title: "Free Computer Classes",
    description: "Empowering rural youth with essential digital literacy, hardware familiarity, software applications, and basic internet typing skills to build a strong future foundation.",
    icon: <Monitor size={30} />,
    theme: "bg-white border-amber-100 hover:border-amber-400 shadow-sm",
    iconColor: "text-amber-800 bg-amber-50 border-amber-100",
    textColor: "text-stone-800",
    descColor: "text-stone-600"
  },
  {
    title: "Free English Speaking Course",
    description: "Building vocabulary, correct pronunciation, and basic conversation skills to boost confidence and public speaking ability in local village students.",
    icon: <Globe size={30} />,
    theme: "bg-white border-amber-100 hover:border-amber-400 shadow-sm",
    iconColor: "text-amber-800 bg-amber-50 border-amber-100",
    textColor: "text-stone-800",
    descColor: "text-stone-600"
  },
  {
    title: "Free Bhagavad Gita Classes",
    description: "Spreading cultural heritage, devotional slokas, and moral character education based on the transcendental wisdom of the Srimad Bhagavad Gita.",
    icon: <BookOpen size={30} />,
    theme: "bg-white border-amber-100 hover:border-amber-400 shadow-sm",
    iconColor: "text-amber-800 bg-amber-50 border-amber-100",
    textColor: "text-stone-800",
    descColor: "text-stone-600"
  },
  {
    title: "Free Academic Tuition Classes",
    description: "Providing auxiliary daily coaching and homework support across Mathematics, Science, Social Sciences, and Hindi/English subjects for children in grades 1 through 8.",
    icon: <GraduationCap size={30} />,
    theme: "bg-white border-amber-100 hover:border-amber-400 shadow-sm",
    iconColor: "text-amber-800 bg-amber-50 border-amber-100",
    textColor: "text-stone-800",
    descColor: "text-stone-600"
  },
];

const Activity = () => {
  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#fffbf4] to-[#fff6e6] py-16 px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-amber-800 font-semibold text-xs tracking-[0.25em] uppercase mb-3 block">
            Our Service
          </span>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-amber-950 tracking-wider">
            Trust Activities
          </h2>
          <div className="h-[2px] w-20 bg-amber-500 mx-auto mt-4"></div>
          <p className="text-stone-600 mt-4 max-w-xl mx-auto font-light text-sm md:text-base">
            Providing free welfare, moral, and technological education programs to support the rural communities of Jaunpur.
          </p>
        </div>

        {/* Activities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {activities.map((activity, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -6 }}
              className={`relative overflow-hidden rounded-3xl border ${activity.theme} p-8 flex flex-col justify-between transition-all duration-300`}
            >
              {/* Sparkle Glow Background */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-2xl pointer-events-none"></div>

              <div>
                {/* Header Row */}
                <div className="flex items-center justify-between mb-8">
                  <div className={`p-3 rounded-2xl border ${activity.iconColor} shrink-0`}>
                    {activity.icon}
                  </div>
                  <span className="text-amber-200/60 text-2xl font-bold font-serif select-none">
                    0{index + 1}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-xl sm:text-2xl font-serif font-bold text-amber-950 mb-4 leading-snug">
                  {activity.title}
                </h3>

                {/* Description */}
                <p className={`${activity.descColor} font-light text-sm leading-relaxed mb-8`}>
                  {activity.description}
                </p>
              </div>

              {/* Decorative Card Footer */}
              <div className="border-t border-amber-100 pt-4 flex items-center justify-between text-xs text-stone-500 font-semibold uppercase tracking-wider">
                <span>RKGVK Seva</span>
                <span className="text-amber-700">★ Free</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default Activity;
